import { Component } from '@theme/component';
import { seedCartCache } from '@theme/cart-api';
import { ThemeEvents, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';

/**
 * Format cents using Shopify money format placeholder.
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  /**
   * @param {number} value
   * @param {number} precision
   * @param {string} thousands
   * @param {string} decimal
   * @returns {string}
   */
  function formatWithDelimiters(value, precision, thousands, decimal) {
    if (Number.isNaN(value) || value === null) {
      return '0';
    }

    const number = (value / 100).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const centsPart = parts[1];

    if (!centsPart) {
      return dollars;
    }

    return dollars + decimal + centsPart;
  }

  const value = Number(cents);
  const defaultAmount = formatWithDelimiters(value, 2, ',', '.');

  if (!format || !format.includes('{{')) {
    return defaultAmount;
  }

  const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
  const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

  /** @type {Record<string, string>} */
  const formats = {
    amount: formatWithDelimiters(value, 2, ',', '.'),
    amount_no_decimals: formatWithDelimiters(value, 0, ',', '.'),
    amount_with_comma_separator: formatWithDelimiters(value, 2, '.', ','),
    amount_no_decimals_with_comma_separator: formatWithDelimiters(value, 0, '.', ','),
    amount_without_trailing_zeros: formatWithDelimiters(value, 2, ',', '.').replace(/\.00$/, ''),
    amount_with_comma_separator_without_trailing_zeros: formatWithDelimiters(value, 2, '.', ',').replace(/,00$/, ''),
    amount_with_space_separator: formatWithDelimiters(value, 2, ' ', ','),
    amount_with_period_and_space_separator: formatWithDelimiters(value, 2, ' ', '.'),
    amount_no_decimals_with_space_separator: formatWithDelimiters(value, 0, ' ', ','),
    amount_no_decimals_with_period_and_space_separator: formatWithDelimiters(value, 0, ' ', '.'),
  };

  return format.replace(/\{\{\s*\w+\s*\}\}/, formats[placeholder] || defaultAmount);
}

/**
 * Complete the Look matching products bundle.
 * @extends Component
 */
class MatchingProductsBundle extends Component {
  #bindingsAbort = null;
  /* bundlechangesontjctheme: blocks a second click while the bundle request is in flight. */
  #submitting = false;
  #cartItems = [];
  /** @type {AbortController | null} */
  #fetchAbort = null;

  /** @type {((event: VariantUpdateEvent) => void) | null} */
  #variantUpdateHandler = null;
  /** @type {AbortController | null} Live TV card listeners (card dropdowns, live price). */
  #ltvAbort = null;
  /** Cross-fade the next header/summary update (Live TV price drop). */
  #animateNext = false;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
    if (this.dataset.cartMode === 'true') return;
    // Live TV: the card owns the on-air variant and price; PDP variant events and section
    // re-renders do not apply to the Live TV page section.
    if (this.dataset.ltvMainLine === 'true') {
      this.#ltvInit();
      return;
    }

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    this.#variantUpdateHandler = this.#onVariantUpdate;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.dataset.cartMode === 'true') this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#bindingsAbort?.abort();
    this.#ltvAbort?.abort();
    this.#ltvAbort = null;
    this.#fetchAbort?.abort();
    this.#fetchAbort = null;

    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection && this.#variantUpdateHandler) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#variantUpdateHandler);
    }
  }

  /** @param {VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (!this.#isEventForThisProduct(event)) {
      return;
    }

    const resource = event.detail.resource;
    const variantId = resource?.id;
    if (!variantId) return;

    const htmlDoc = event.detail.data.html;
    if (htmlDoc) {
      const newBundle = this.#findBundleInResponse(htmlDoc);
      if (newBundle) {
        this.#applyBundleFromElement(newBundle);
        this.#syncMainRowFromVariant(resource);
        return;
      }
    }

    this.#fetchAndRender(String(variantId), resource);
  };

  /**
   * Keep the main bundle row in sync with the selected PDP variant (image, price, variant id).
   * @param {Record<string, unknown> | null | undefined} variant
   */
  #syncMainRowFromVariant(variant) {
    if (!variant || variant.id == null) return;

    const mainItem = this.querySelector('.mp-bundle__item[data-main="true"]');
    if (!(mainItem instanceof HTMLElement)) return;

    const cb = mainItem.querySelector('.mp-bundle__cb');
    if (cb instanceof HTMLInputElement) {
      cb.dataset.variantId = String(variant.id);
      cb.dataset.sku = typeof variant.sku === 'string' ? variant.sku : '';
      cb.dataset.price = String(variant.price ?? 0);
      cb.dataset.compare = String(variant.compare_at_price ?? variant.price ?? 0);
      const available = variant.available !== false;
      cb.dataset.available = available ? 'true' : 'false';
      cb.disabled = !available;
      if (!available) cb.checked = false;
    }

    // The freshly rendered bundle already contains the selected variant's
    // custom.variant_images media. Only override it when that metafield is
    // explicitly included in the variant event payload.
    if (Array.isArray(variant.mf_images)) {
      const previewSrc = this.#getVariantImageSrc(variant);
      const img = mainItem.querySelector('.mp-bundle__img');
      if (img instanceof HTMLImageElement) {
        if (previewSrc) {
          img.hidden = false;
          this.#setResponsiveImage(img, previewSrc);
        } else {
          img.hidden = true;
          img.removeAttribute('src');
          img.removeAttribute('srcset');
        }
      }
    }

    const sale = mainItem.querySelector('.mp-sale');
    const cmp = mainItem.querySelector('.mp-compare');
    const price = Number(variant.price ?? 0);
    const compare = Number(variant.compare_at_price ?? 0);

    if (sale instanceof HTMLElement) {
      sale.textContent = this.#fmt(price);
    }

    if (cmp instanceof HTMLElement) {
      if (compare > price) {
        cmp.textContent = this.#fmt(compare);
        cmp.hidden = false;
      } else {
        cmp.hidden = true;
      }
    }

    this.dataset.variantId = String(variant.id);
    this.#syncProductLink(mainItem);
    this.#updateHeader();
  };

  /**
   * Resolve bundle image URL from variant update payload.
   * @param {Record<string, unknown> | null | undefined} variant
   * @returns {string}
   */
  #getVariantImageSrc(variant) {
    if (!variant) return '';

    const mfImages = variant.mf_images;
    if (Array.isArray(mfImages) && typeof mfImages[0] === 'string' && mfImages[0]) {
      return mfImages[0];
    }

    return '';
  }

  /** @param {VariantUpdateEvent} event */
  #isEventForThisProduct(event) {
    const eventProductId = event.detail.data.productId || event.detail.data.newProduct?.id;
    if (eventProductId && String(eventProductId) !== String(this.dataset.productId)) {
      return false;
    }

    const eventTarget = event.target;
    if (
      eventTarget instanceof HTMLElement &&
      eventTarget.dataset.productId &&
      String(eventTarget.dataset.productId) !== String(this.dataset.productId)
    ) {
      return false;
    }

    return true;
  }

  /** @returns {string} */
  #getSectionId() {
    const fromData = this.dataset.sectionId || '';
    if (fromData) return fromData.replace(/^shopify-section-/, '');

    const sectionEl = this.closest('.shopify-section');
    return sectionEl?.id?.replace(/^shopify-section-/, '') || '';
  }

  /**
   * Re-render bundle for the selected variant via Section Rendering API.
   * @param {string} variantId
   * @param {Record<string, unknown> | null | undefined} [resource]
   */
  async #fetchAndRender(variantId, resource) {
    const handle = this.dataset.productHandle;
    const sectionId = this.#getSectionId();
    if (!handle || !sectionId) return;

    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();

    const url = `/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(sectionId)}&variant=${encodeURIComponent(variantId)}`;

    try {
      const response = await fetch(url, { signal: this.#fetchAbort.signal });
      if (!response.ok) throw new Error(`Section fetch failed (${response.status})`);

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const newBundle = this.#findBundleInResponse(html);
      this.#applyBundleFromElement(newBundle);
      if (resource) {
        this.#syncMainRowFromVariant(resource);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('[matching-products-bundle] section fetch failed:', error);
    }
  }

  /**
   * @param {Element | null} newBundle
   */
  #applyBundleFromElement(newBundle) {
    if (!newBundle) {
      this.hidden = true;
      this.querySelector('[data-ref="bundleRoot"]')?.remove();
      return;
    }

    const newVariantId = newBundle instanceof HTMLElement ? newBundle.dataset.variantId || '' : '';
    const newRoot = newBundle.querySelector('[data-ref="bundleRoot"]');
    const currentRoot = this.querySelector('[data-ref="bundleRoot"]');
    const hasContent = !!newRoot;

    this.hidden = !hasContent;

    if (!hasContent) {
      if (currentRoot) currentRoot.remove();
      this.dataset.variantId = newVariantId;
      return;
    }

    const currentVariantId = this.dataset.variantId || '';
    const contentChanged =
      newVariantId !== currentVariantId ||
      !currentRoot ||
      currentRoot.innerHTML !== newRoot.innerHTML;

    if (!contentChanged) return;

    this.dataset.variantId = newVariantId;
    if (newBundle instanceof HTMLElement) {
      this.dataset.discountCode = newBundle.dataset.discountCode || this.dataset.discountCode || '';
      this.dataset.discountPct = newBundle.dataset.discountPct || this.dataset.discountPct || '0.1';
      this.dataset.discountPercent = newBundle.dataset.discountPercent || this.dataset.discountPercent || '10';
      this.dataset.minItems = newBundle.dataset.minItems || this.dataset.minItems || '2';
      this.dataset.moneyFormat = newBundle.dataset.moneyFormat || this.dataset.moneyFormat || '{{amount}}';
    }

    if (currentRoot) {
      currentRoot.replaceWith(newRoot.cloneNode(true));
    } else {
      this.appendChild(newRoot.cloneNode(true));
    }

    this.#init();
  }

  /**
   * Find this bundle instance in a fetched HTML document.
   * @param {ParentNode} htmlDoc
   * @returns {Element | null}
   */
  #findBundleInResponse(htmlDoc) {
    const sectionId = this.#getSectionId();
    /** @type {ParentNode} */
    let searchRoot = htmlDoc;

    if (sectionId) {
      const sectionEl = htmlDoc.querySelector(`#shopify-section-${CSS.escape(sectionId)}`);
      if (sectionEl) searchRoot = sectionEl;
    }

    if (this.id) {
      const byId = searchRoot.querySelector(`#${CSS.escape(this.id)}`);
      if (byId) return byId;
    }

    const productId = this.dataset.productId;
    if (productId) {
      return searchRoot.querySelector(`matching-products-bundle[data-product-id="${productId}"]`);
    }

    return searchRoot.querySelector('matching-products-bundle');
  }

  #init() {
    this.#bindingsAbort?.abort();
    this.#bindingsAbort = new AbortController();
    if (this.dataset.cartMode === 'true') {
      const state = this.querySelector('[data-cart-state]');
      this.#cartItems = JSON.parse(state?.textContent || '{"items":[]}').items;
    }
    const root = this.querySelector('[data-ref="bundleRoot"]');
    if (!root) return;

    root.querySelectorAll('.mp-bundle__item').forEach((item) => {
      if (item instanceof HTMLElement) {
        delete item.dataset.bound;
      }
      this.#bindItem(item);
    });

    const cta = this.querySelector('[data-ref="cta"]');
    if (cta instanceof HTMLButtonElement) {
      delete cta.dataset.bound;
    }

    this.#updateHeader();
    this.#bindCta();
  }

  /** @param {Element} item */
  #bindItem(item) {
    if (item instanceof HTMLElement && item.dataset.bound === 'true') return;
    if (item instanceof HTMLElement) item.dataset.bound = 'true';

    const cb = item.querySelector('.mp-bundle__cb');
    const titleLink = item.querySelector('.mp-bundle__name');
    if (titleLink instanceof HTMLAnchorElement) {
      titleLink.addEventListener('click', (event) => event.stopPropagation(), { signal: this.#bindingsAbort.signal });
    }

    // The row is a <label>, so a click beside a dropdown (e.g. to dismiss it) would toggle the checkbox.
    const optionsRow = item.querySelector('.mp-bundle__options');
    if (optionsRow instanceof HTMLElement) {
      optionsRow.addEventListener('click', (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('select')) event.preventDefault();
      }, { signal: this.#bindingsAbort.signal });
    }

    this.#syncItemAvailability(item);

    if (cb instanceof HTMLInputElement) {
      if (cb.dataset.cartAnchor === 'true' || this.dataset.cartComplete === 'true') {
        cb.addEventListener('click', (event) => event.preventDefault(), { signal: this.#bindingsAbort.signal });
      }
      cb.addEventListener('change', () => {
        const imgWrap = item.querySelector('.mp-bundle__img-wrap');
        if (imgWrap instanceof HTMLElement) {
          imgWrap.style.opacity = cb.checked ? '1' : '0.35';
        }
        this.#updateHeader();
      }, { signal: this.#bindingsAbort.signal });
    }

    const selects = Array.from(item.querySelectorAll('.mp-bundle__select'));
    if (this.dataset.cartComplete === 'true') selects.forEach((select) => { select.disabled = true; });
    const variantData = item.querySelector('[data-bundle-variants]');
    if (!variantData || !(cb instanceof HTMLInputElement)) return;
    const variants = JSON.parse(variantData.textContent || '[]');

    const syncOptions = () => {
      const values = selects.map((control) => control.value);
      selects.forEach((select) => {
        const index = Number(select.dataset.optionIndex);
        Array.from(select.options).forEach((option) => {
          // Live TV option-type placeholder ("Colour"): never selectable, never "out of stock".
          if (option.value === '') return;
          // Follow option order: size depends on shape/metal, while changing
          // shape can still switch to another available metal/size combination.
          // An unpicked earlier option (placeholder) matches any value.
          option.disabled = !variants.some((variant) => variant.available && variant.options[index] === option.value &&
            variant.options.every((value, i) => i >= index || values[i] === '' || value === values[i]));
          const label = option.dataset.optionLabel || option.textContent.replace(/ \(out of stock\)$/, '');
          option.dataset.optionLabel = label;
          option.textContent = `${label}${option.disabled ? ' (out of stock)' : ''}`;
        });
      });
    };
    syncOptions();

    selects.forEach((select) => select.addEventListener('change', () => {
      const values = selects.map((control) => control.value);
      const index = Number(select.dataset.optionIndex);
      // Prefer the exact combination, otherwise choose an available combination
      // for the changed option and bring the other controls along with it.
      const variant = variants.find((candidate) => candidate.available && candidate.options.every((value, i) => value === values[i]))
        || variants.find((candidate) => candidate.available && candidate.options[index] === select.value &&
          candidate.options.every((value, i) => i >= index || values[i] === '' || value === values[i]));
      if (!variant) return;

      // A dropdown still on its option-type placeholder stays on it until the shopper picks.
      selects.forEach((control, i) => {
        if (control === select || control.value !== '') control.value = variant.options[i];
      });
      cb.dataset.variantId = String(variant.id);
      cb.dataset.sku = variant.sku || '';
      cb.dataset.price = String(variant.price);
      cb.dataset.compare = String(variant.compare);
      cb.dataset.available = String(variant.available);
      const wasDisabled = cb.disabled;
      cb.disabled = !variant.available;
      if (wasDisabled && variant.available) cb.checked = true;

      const img = item.querySelector('.mp-bundle__img');
      if (img instanceof HTMLImageElement) {
        img.hidden = !variant.image;
        if (variant.image) this.#setResponsiveImage(img, variant.image);
      }
      const sale = item.querySelector('.mp-sale');
      let compare = item.querySelector('.mp-compare');
      // Live TV: #ltvRenderItemPrices (via #updateHeader below) writes bundle price + struck original.
      if (this.dataset.ltvMainLine === 'true') compare = null;
      else if (sale) sale.textContent = this.#fmt(variant.price);
      if (this.dataset.ltvMainLine !== 'true' && !compare && variant.compare > variant.price) {
        compare = document.createElement('span');
        compare.className = 'mp-compare';
        item.querySelector('.mp-bundle__prices')?.appendChild(compare);
      }
      if (compare) {
        compare.textContent = this.#fmt(variant.compare);
        compare.hidden = variant.compare <= variant.price;
      }
      syncOptions();
      this.#syncItemAvailability(item);
      this.#updateHeader();
    }, { signal: this.#bindingsAbort.signal }));
  }

  /** @param {Element} item */
  #syncProductLink(item) {
    const link = item.querySelector('.mp-bundle__name');
    const variantId = item.querySelector('.mp-bundle__cb')?.dataset.variantId;
    if (!(link instanceof HTMLAnchorElement) || !variantId) return;

    const url = new URL(link.href);
    url.searchParams.set('variant', variantId);
    link.href = url.href;
    link.dataset.productUrl = url.href;
  }

  /** @param {Element} item */
  #syncItemAvailability(item) {
    this.#syncProductLink(item);
    const cb = item.querySelector('.mp-bundle__cb');
    if (!(cb instanceof HTMLInputElement)) return;
    cb.disabled = cb.dataset.available === 'false';
    if (cb.disabled) cb.checked = false;
    const imgWrap = item.querySelector('.mp-bundle__img-wrap');
    if (imgWrap instanceof HTMLElement) imgWrap.style.opacity = cb.checked ? '1' : '0.35';
  }

  #bindCta() {
    const cta = this.querySelector('[data-ref="cta"]');
    if (!(cta instanceof HTMLButtonElement) || cta.dataset.bound === 'true') return;
    cta.dataset.bound = 'true';

    cta.addEventListener('click', () => {
      if (this.dataset.cartMode === 'true') {
        this.#completeCartBundle();
      } else {
        this.#addBundleToCart();
      }
    }, { signal: this.#bindingsAbort.signal });
  }

  /** @returns {HTMLInputElement[]} */
  #getChecked() {
    return Array.from(this.querySelectorAll('.mp-bundle__cb:checked:not(:disabled)')).filter(
      (el) => el instanceof HTMLInputElement
    );
  }

  #updateHeader() {
    const checked = this.#getChecked();
    const minItems = parseInt(this.dataset.minItems || '2', 10);
    const discountPct = parseFloat(this.dataset.discountPct || '0.1');

    const completed = this.dataset.cartMode === 'true' && this.dataset.cartComplete === 'true';
    const cartLineFor = (cb) => this.#cartItems.find((item) => String(item.variant_id) === cb.dataset.variantId);
    let total = 0;
    checked.forEach((cb) => {
      total += completed ? Number(cartLineFor(cb)?.original_price ?? cb.dataset.price ?? 0) : parseInt(cb.dataset.price || '0', 10);
    });

    if (this.dataset.cartMode === 'true') {
      const inBag = new Set(this.#cartItems.map((item) => String(item.variant_id)));
      const missingCount = checked.filter((cb) => !inBag.has(cb.dataset.variantId)).length;
      const label = this.querySelector('[data-ref="cartCtaText"]');
      if (label) {
        const action = missingCount ? `Add missing item${missingCount === 1 ? '' : 's'}` : 'Apply bundle saving';
        label.textContent = completed ? 'Bundle in your bag' : `${action} | Save ${this.dataset.discountPercent}%`;
      }
      this.querySelectorAll('.mp-bundle__item').forEach((item) => {
        const badge = item.querySelector('[data-ref="inCart"]');
        const cb = item.querySelector('.mp-bundle__cb');
        if (badge && cb) badge.hidden = !inBag.has(cb.dataset.variantId);
      });
      const cta = this.querySelector('[data-ref="cta"]');
      if (cta) cta.disabled = completed || this.#submitting || checked.length < minItems;
    }

    const count = checked.length;
    const discounted = completed
      ? checked.reduce((sum, cb) => sum + Number(cartLineFor(cb)?.final_price ?? cb.dataset.price ?? 0), 0)
      : Math.round(total * (1 - discountPct));
    const qualifies = count >= minItems;

    const nowEl = this.querySelector('[data-ref="headerNow"]');
    const wasEl = this.querySelector('[data-ref="headerWas"]');
    const animate = this.#animateNext;
    this.#animateNext = false;
    if (this.dataset.ltvMainLine === 'true') this.#ltvRenderItemPrices(qualifies, discountPct, animate);
    this.querySelectorAll('[data-ref="headerSaving"], [data-ref="summarySaving"]').forEach((element) => {
      if (element instanceof HTMLElement) this.#setText(element, this.#fmt(qualifies ? total - discounted : 0), animate);
    });
    const countEl = this.querySelector('[data-ref="ctaCount"]');
    const minMsg = this.querySelector('[data-ref="minMsg"]');

    if (nowEl instanceof HTMLElement) {
      this.#setText(nowEl, qualifies ? this.#fmt(discounted) : this.#fmt(total), animate);
      nowEl.style.color = qualifies ? '' : 'var(--cx-color-grey-800)';
    }

    if (wasEl instanceof HTMLElement) {
      this.#setText(wasEl, this.#fmt(total), animate);
      wasEl.hidden = false;
    }

    if (countEl instanceof HTMLElement) {
      countEl.textContent = String(count);
    }

    if (minMsg instanceof HTMLElement) {
      minMsg.hidden = qualifies;
    }

  }

  async #completeCartBundle() {
    if (this.#submitting || this.dataset.cartComplete === 'true') return;
    const checked = this.#getChecked();
    if (checked.length < Number(this.dataset.minItems || 2)) return;
    this.#submitting = true;
    const errorMessage = this.querySelector('[data-ref="cartError"]');
    if (errorMessage) errorMessage.hidden = true;
    const controls = Array.from(this.querySelectorAll('input, select'));
    const disabledStates = controls.map((control) => control.disabled);
    controls.forEach((control) => { control.disabled = true; });
    this.setAttribute('aria-busy', 'true');
    const cta = this.querySelector('[data-ref="cta"]');
    if (cta) cta.disabled = true;
    const root = window.Shopify?.routes?.root || '/';
    try {
      const { completeCartBundle } = await import('@theme/matching-products-bundle-cart');
      const { cart, addedCount } = await completeCartBundle({
        selection: checked.map((cb) => ({ id: cb.dataset.variantId, sku: cb.dataset.sku })),
        anchorVariantId: this.dataset.variantId,
        discountCode: this.dataset.discountCode || '',
        discountPercent: Number(this.dataset.discountPercent || 10),
        root,
      });
      seedCartCache(cart);
      document.dispatchEvent(new CartUpdateEvent(cart, this.id, {
        action: addedCount ? 'add' : 'update', itemCount: cart.item_count, delta: addedCount,
      }));
    } catch (error) {
      if (errorMessage) {
        errorMessage.textContent = error instanceof Error ? error.message : 'Could not update your bundle. Please try again.';
        errorMessage.hidden = false;
      }
      // The following refresh can remove this offer. Keep the failure visible
      // until it is acknowledged even when the offer no longer exists.
      alert(error instanceof Error ? error.message : 'Could not update your bundle. Please try again.');
      // A later request can fail after an earlier mutation succeeded. Refresh the
      // actual bag so a retry never relies on stale quantities or line properties.
      try {
        const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
        if (response.ok) {
          const cart = await response.json();
          seedCartCache(cart);
          document.dispatchEvent(new CartUpdateEvent(cart, this.id, { action: 'update', itemCount: cart.item_count }));
        }
      } catch (_) { /* Keep the actionable inline error when the network is unavailable. */ }
    } finally {
      this.#submitting = false;
      controls.forEach((control, index) => { control.disabled = disabledStates[index]; });
      this.removeAttribute('aria-busy');
      this.#updateHeader();
    }
  }

  async #addBundleToCart() {
    console.info('[matching-products-bundle] add clicked');
    if (this.#submitting) return;
    const checked = this.#getChecked();
    const minItems = parseInt(this.dataset.minItems || '2', 10);
    const minMsg = this.querySelector('[data-ref="minMsg"]');
    const cta = this.querySelector('[data-ref="cta"]');
    const discountCode = this.dataset.discountCode || '';
    const root = window.Shopify?.routes?.root || '/';
    let errorMessage = this.querySelector('[data-bundle-add-error]');
    if (!errorMessage) {
      errorMessage = document.createElement('p');
      errorMessage.setAttribute('data-bundle-add-error', '');
      errorMessage.setAttribute('role', 'alert');
      cta?.insertAdjacentElement('beforebegin', errorMessage);
    }
    errorMessage.hidden = true;

    if (checked.length < minItems) {
      console.warn(`[matching-products-bundle] fewer than ${minItems} items ticked; nothing was added`);
      if (minMsg instanceof HTMLElement) minMsg.hidden = false;
      return;
    }

    const bundleSkus = checked
      .map((cb) => cb.dataset.sku?.trim())
      .filter((sku) => sku);
    const discountPercent = parseInt(this.dataset.discountPercent || '10', 10);
    const completeYourBundle = {
      sku: bundleSkus.join(','),
      discount: discountPercent,
    };

    const bundleId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const bundleProperties = {
      _bundle_id: bundleId,
      _complete_your_bundle: JSON.stringify(completeYourBundle),
    };
    let items = checked.map((cb) => ({
      id: parseInt(cb.dataset.variantId || cb.value, 10),
      quantity: 1,
      properties: { ...bundleProperties },
    }));

    // Live TV card: the on-air item is added by the card's own rules (TV flags, auction code,
    // Budget Pay), merged with the bundle properties. The other items stay plain, as on the PDP.
    // Never add the on-air item without its TV flags: it would be booked as a full-price sale.
    const isLtvMainLine = this.dataset.ltvMainLine === 'true';
    if (isLtvMainLine) {
      const mainCheckbox = checked.find((cb) => cb.closest('[data-main]'));
      if (mainCheckbox) {
        if (typeof window.ltvPrepareBundleMainLine !== 'function') {
          console.warn('[matching-products-bundle] live-tv-card-forms.js is not ready; nothing was added');
          return;
        }
        const mainItem = await window.ltvPrepareBundleMainLine(this.closest('article.currently-on-air-card'));
        if (!mainItem) {
          console.warn('[matching-products-bundle] the on-air item could not be prepared; nothing was added');
          return;
        }
        if (!mainItem.properties || !mainItem.properties._itemSource) {
          console.warn('[matching-products-bundle] the on-air item has no _itemSource; nothing was added', mainItem);
          return;
        }
        const mainIndex = checked.indexOf(mainCheckbox);
        items[mainIndex] = { ...mainItem, properties: { ...mainItem.properties, ...bundleProperties } };
      }
    }

    this.#submitting = true;
    this.setAttribute('aria-busy', 'true');
    if (cta instanceof HTMLButtonElement) cta.disabled = true;

    let added = false;
    const request = async (path, body) => {
      const response = await fetch(`${root}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || `Could not update your bag (${response.status}).`);
      }
      return data;
    };

    try {
      const { reuseMatchingBundle } = await import('./matching-products-bundle-cart.js');
      const currentCart = await request('cart.js');
      // Reuse would overwrite the on-air line's properties with an older line's, so skip it on Live TV.
      if (!isLtvMainLine) items = reuseMatchingBundle(items, currentCart);
      await request('cart/add.js', { items });
      added = true;
      let cart = await request('cart.js');
      const codes = (cart.discount_codes || []).map((entry) => entry.code);
      if (discountCode && !codes.some((code) => code.toLowerCase() === discountCode.toLowerCase())) {
        cart = await request('cart/update.js', { discount: [...codes, discountCode].join(',') });
      }
      seedCartCache(cart);

      document.dispatchEvent(
        new CartUpdateEvent(cart, this.id, {
          action: 'add',
          itemCount: cart.item_count,
          delta: items.length,
        })
      );
    } catch (error) {
      console.error('[matching-products-bundle]', error);
      const message = error instanceof Error ? error.message : 'Could not update your bag. Please try again.';
      errorMessage.textContent = added
        ? `Your bundle was added, but the bag could not finish updating. Please review your bag before adding again. ${message}`
        : message;
      errorMessage.hidden = false;
      // A rejected add can still change quantities. Refresh without retrying the add.
      try {
        const cart = await request('cart.js');
        seedCartCache(cart);
        document.dispatchEvent(new CartUpdateEvent(cart, this.id, { action: 'update', itemCount: cart.item_count }));
      } catch (_) { /* Keep the original error visible if the refresh also fails. */ }
    } finally {
      this.#submitting = false;
      this.removeAttribute('aria-busy');
      if (cta instanceof HTMLButtonElement) cta.disabled = false;
    }
  }

  /**
   * Set a price text. With animate, the old figure fades out while the new one fades in (1s, the
   * Figma price-drop prototype); the logical value is kept in data-xf-text while both are shown.
   * @param {HTMLElement} el
   * @param {string} text
   * @param {boolean} [animate]
   */
  #setText(el, text, animate = false) {
    const current = el.dataset.xfText ?? el.textContent;
    if (current === text && el.dataset.xfText === undefined) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduceMotion || current === text) {
      delete el.dataset.xfText;
      el.textContent = text;
      return;
    }
    el.dataset.xfText = text;
    const next = document.createElement('span');
    next.className = 'mp-xfade mp-xfade--in';
    next.textContent = text;
    const prev = document.createElement('span');
    prev.className = 'mp-xfade mp-xfade--out';
    prev.setAttribute('aria-hidden', 'true');
    prev.textContent = current;
    el.replaceChildren(next, prev);
    window.setTimeout(() => {
      if (el.dataset.xfText !== text || !el.contains(next)) return;
      delete el.dataset.xfText;
      el.textContent = text;
    }, 1000);
  }

  /** Live TV: follow the card's dropdowns and live price. */
  #ltvInit() {
    const card = this.closest('article.currently-on-air-card');
    const form = card?.querySelector('form.currently-on-air-card__form');
    if (!card || !(form instanceof HTMLFormElement)) return;
    this.#ltvAbort?.abort();
    this.#ltvAbort = new AbortController();
    const { signal } = this.#ltvAbort;

    let queued = false;
    const refresh = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.#ltvMirrorSelects(form);
        this.#ltvSyncMainVariant(form);
      });
    };
    refresh();
    // Card dropdowns change the variant; GLA/MLA rebuilds replace options or show/hide rows.
    form.addEventListener('change', refresh, { signal });
    const observer = new MutationObserver(refresh);
    observer.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled', 'value'] });
    signal.addEventListener('abort', () => observer.disconnect());

    // ltv-price-drop.js reports each change of the card price; drop = same item, lower price.
    card.addEventListener('ltv:price-change', (event) => {
      const detail = /** @type {CustomEvent} */ (event).detail || {};
      this.#ltvSetMainPrice(Number(detail.price), detail.drop === true);
    }, { signal });
    const priceEl = card.querySelector('.currently-on-air-card__price');
    const startPrice = typeof window.ltvParsePriceCents === 'function' ? window.ltvParsePriceCents(priceEl?.textContent) : null;
    if (startPrice) this.#ltvSetMainPrice(startPrice, false);
  }

  /** @param {HTMLFormElement} form */
  #ltvCardSelects(form) {
    return Array.from(form.querySelectorAll('select')).filter(
      (select) => select instanceof HTMLSelectElement && !select.closest('[hidden]') && select.options.length > 0
    );
  }

  /**
   * Copy the card's visible dropdowns into the on-air row. Picking in the copy picks on the card,
   * so the card's own handlers set the variant, auction code and price exactly as for Add To Bag.
   * @param {HTMLFormElement} form
   */
  #ltvMirrorSelects(form) {
    const holder = this.querySelector('[data-ltv-main-options]');
    if (!(holder instanceof HTMLElement)) return;
    const names = (holder.dataset.optionNames || '').split('|').filter((name) => name && name !== 'Title');
    const iconTemplate = this.querySelector('template[data-ltv-select-icon]');
    const icon = iconTemplate instanceof HTMLTemplateElement ? iconTemplate.content.firstElementChild : null;
    const cardSelects = this.#ltvCardSelects(form);
    holder.replaceChildren();
    holder.hidden = cardSelects.length === 0;
    cardSelects.forEach((cardSelect, index) => {
      const wrap = document.createElement('div');
      wrap.className = 'mp-bundle__select-wrap';
      const select = document.createElement('select');
      select.className = 'mp-bundle__select';
      select.disabled = cardSelect.disabled;
      select.setAttribute('aria-label', names[index] || cardSelect.getAttribute('aria-label') || '');
      Array.from(cardSelect.options).forEach((cardOption) => {
        const isPlaceholder = cardOption.value === '';
        const label = isPlaceholder && names[index] ? names[index] : cardOption.textContent || '';
        const option = new Option(label, cardOption.value);
        option.disabled = isPlaceholder || cardOption.disabled;
        option.selected = cardOption.selected;
        select.appendChild(option);
      });
      select.addEventListener('change', () => {
        cardSelect.value = select.value;
        cardSelect.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrap.appendChild(select);
      if (icon) wrap.appendChild(icon.cloneNode(true));
      holder.appendChild(wrap);
    });
  }

  /** @param {HTMLFormElement} form */
  #ltvSyncMainVariant(form) {
    const cb = this.querySelector('.mp-bundle__item[data-main="true"] .mp-bundle__cb');
    if (!(cb instanceof HTMLInputElement)) return;
    const idInput = form.querySelector('input[name="id"]');
    const variantId = idInput instanceof HTMLInputElement ? idInput.value.trim() : '';
    if (!variantId || variantId === cb.dataset.variantId) return;
    cb.dataset.variantId = variantId;
    this.dataset.variantId = variantId;
    let sku = '';
    this.#ltvCardSelects(form).forEach((select) => {
      const opt = select.options[select.selectedIndex];
      const optSku = opt?.dataset.sku || opt?.dataset.stockCode || '';
      if (opt && opt.value !== '' && optSku) sku = optSku;
    });
    if (sku) cb.dataset.sku = sku;
    const item = cb.closest('.mp-bundle__item');
    if (item) this.#syncProductLink(item);
  }

  /**
   * @param {number} cents
   * @param {boolean} animate
   */
  #ltvSetMainPrice(cents, animate) {
    if (!Number.isFinite(cents) || cents <= 0) return;
    const main = this.querySelector('.mp-bundle__item[data-main="true"]');
    const cb = main?.querySelector('.mp-bundle__cb');
    if (!main || !(cb instanceof HTMLInputElement) || Number(cb.dataset.price) === cents) return;
    cb.dataset.price = String(cents);
    this.#animateNext = animate;
    this.#updateHeader();
  }

  /**
   * Live TV: each ticked row shows its bundle price with the original struck through, while the
   * bundle qualifies; otherwise just its price. Only the on-air row moves on a price drop.
   * @param {boolean} qualifies
   * @param {number} discountPct e.g. 0.1
   * @param {boolean} animate
   */
  #ltvRenderItemPrices(qualifies, discountPct, animate) {
    this.querySelectorAll('.mp-bundle__item').forEach((item) => {
      const cb = item.querySelector('.mp-bundle__cb');
      const sale = item.querySelector('.mp-sale');
      const was = item.querySelector('[data-ref="itemWas"]');
      if (!(cb instanceof HTMLInputElement) || !(sale instanceof HTMLElement)) return;
      const price = parseInt(cb.dataset.price || '0', 10);
      const discounted = qualifies && cb.checked && !cb.disabled;
      const rowAnimate = animate && item.dataset.main === 'true';
      this.#setText(sale, this.#fmt(discounted ? Math.round(price * (1 - discountPct)) : price), rowAnimate);
      if (was instanceof HTMLElement) {
        this.#setText(was, this.#fmt(price), rowAnimate);
        was.hidden = !discounted;
      }
    });
  }

  /** @param {number} cents */
  #fmt(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}');
  }

  /** @param {string} src */
  #imageUrlWithWidth(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_error) {
      const separator = src.includes('?') ? '&' : '?';
      return `${src}${separator}width=${width}`;
    }
  }

  /**
   * @param {HTMLImageElement} img
   * @param {string} src
   */
  #setResponsiveImage(img, src) {
    const mobileWidth = Number.parseInt(this.dataset.imageMobileWidth || '200', 10) || 200;
    const tabletWidth = Number.parseInt(this.dataset.imageTabletWidth || '200', 10) || 200;
    const desktopWidth = Number.parseInt(this.dataset.imageWidth || '200', 10) || 200;
    const picture = img.closest('picture');

    if (picture instanceof HTMLPictureElement) {
      const mobileSource = picture.querySelector('source[media="(max-width: 767px)"]');
      const tabletSource = picture.querySelector('source[media="(max-width: 1023px)"]');

      if (mobileSource instanceof HTMLSourceElement) {
        mobileSource.srcset = this.#imageUrlWithWidth(src, mobileWidth);
      }
      if (tabletSource instanceof HTMLSourceElement) {
        tabletSource.srcset = this.#imageUrlWithWidth(src, tabletWidth);
      }
    }

    img.src = this.#imageUrlWithWidth(src, desktopWidth);
  }
}

if (!customElements.get('matching-products-bundle')) {
  customElements.define('matching-products-bundle', MatchingProductsBundle);
}
